import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Calendar, 
  Clock, 
  Video, 
  Phone, 
  Check, 
  X, 
  MessageSquare,
  User,
  Building2,
  ChevronRight,
  FileText,
  Star,
  Plus,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InterviewsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, upcoming, past, pending

  const isPM = user?.role === 'project_manager';
  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';

  useEffect(() => {
    fetchInterviews();
    
    // Auto-select interview from URL
    const interviewId = searchParams.get('id');
    if (interviewId) {
      // Will be handled after interviews are loaded
    }
  }, []);

  useEffect(() => {
    const interviewId = searchParams.get('id');
    if (interviewId && interviews.length > 0) {
      const interview = interviews.find(i => i.id === interviewId);
      if (interview) {
        setSelectedInterview(interview);
      }
    }
  }, [interviews, searchParams]);

  const fetchInterviews = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/interviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterviews(response.data.interviews || []);
    } catch (error) {
      console.error('Failed to fetch interviews:', error);
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'pending_pm': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'pending': return 'Pending RM Response';
      case 'pending_pm': return 'Pending PM Confirmation';
      case 'cancelled': return 'Cancelled';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const getPlatformIcon = (platform) => {
    if (['Google Meet', 'Microsoft Teams', 'Zoom'].includes(platform)) {
      return <Video className="w-4 h-4" />;
    }
    return <Phone className="w-4 h-4" />;
  };

  const filteredInterviews = interviews.filter(interview => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['pending', 'pending_pm'].includes(interview.status);
    if (filter === 'upcoming') {
      if (!interview.confirmed_slot) return false;
      const interviewDate = new Date(`${interview.confirmed_slot.date}T${interview.confirmed_slot.time}`);
      return interviewDate > new Date() && interview.status === 'confirmed';
    }
    if (filter === 'past') {
      if (interview.status === 'completed') return true;
      if (!interview.confirmed_slot) return false;
      const interviewDate = new Date(`${interview.confirmed_slot.date}T${interview.confirmed_slot.time}`);
      return interviewDate < new Date();
    }
    return true;
  });

  // Group interviews by date for calendar view
  const interviewsByDate = filteredInterviews.reduce((acc, interview) => {
    if (interview.confirmed_slot?.date) {
      const date = interview.confirmed_slot.date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(interview);
    }
    return acc;
  }, {});

  const todayInterviews = filteredInterviews.filter(i => {
    if (!i.confirmed_slot?.date) return false;
    const today = new Date().toISOString().split('T')[0];
    return i.confirmed_slot.date === today && i.status === 'confirmed';
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
            Interviews
          </h1>
          <p className="text-gray-600 mt-1">
            {isPM 
              ? 'Schedule and manage interviews with potential consultants'
              : 'Manage interview requests from Project Managers'
            }
          </p>
        </div>

        {/* Today's Interviews Banner */}
        {todayInterviews.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5" />
              Today's Interviews ({todayInterviews.length})
            </h3>
            <div className="flex flex-wrap gap-3">
              {todayInterviews.map(interview => (
                <div 
                  key={interview.id}
                  className="bg-white rounded-lg px-3 py-2 border border-blue-200 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => setSelectedInterview(interview)}
                >
                  <p className="font-medium text-sm">
                    {interview.resource?.first_name} {interview.resource?.last_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {interview.confirmed_slot?.time} • {interview.duration_minutes} min
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'upcoming', label: 'Upcoming' },
            { key: 'past', label: 'Past' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                filter === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 hover:border-blue-400'
              }`}
            >
              {tab.label}
              <span className="ml-2 text-xs opacity-75">
                ({interviews.filter(i => {
                  if (tab.key === 'all') return true;
                  if (tab.key === 'pending') return ['pending', 'pending_pm'].includes(i.status);
                  if (tab.key === 'upcoming') {
                    if (!i.confirmed_slot) return false;
                    return new Date(`${i.confirmed_slot.date}T${i.confirmed_slot.time}`) > new Date() && i.status === 'confirmed';
                  }
                  if (tab.key === 'past') return i.status === 'completed';
                  return true;
                }).length})
              </span>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Interview List */}
          <div className="lg:col-span-2 space-y-4">
            {filteredInterviews.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No interviews {filter !== 'all' && `(${filter})`}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {isPM 
                      ? 'Browse resources and schedule interviews to find the perfect consultant.'
                      : 'Interview requests from Project Managers will appear here.'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredInterviews.map(interview => (
                <Card 
                  key={interview.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedInterview?.id === interview.id 
                      ? 'ring-2 ring-blue-400' 
                      : ''
                  }`}
                  onClick={() => setSelectedInterview(interview)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">
                            {interview.resource?.first_name} {interview.resource?.last_name}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(interview.status)}`}>
                            {getStatusLabel(interview.status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {interview.resource?.role} • {interview.resource?.seniority}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {interview.is_requester 
                              ? interview.resource_company?.name
                              : interview.requester_company?.name
                            }
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {interview.duration_minutes} min
                          </span>
                          {interview.confirmed_slot && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Calendar className="w-3 h-3" />
                              {new Date(interview.confirmed_slot.date).toLocaleDateString()} at {interview.confirmed_slot.time}
                            </span>
                          )}
                          {interview.confirmed_platform && (
                            <span className="flex items-center gap-1">
                              {getPlatformIcon(interview.confirmed_platform)}
                              {interview.confirmed_platform}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Interview Details Sidebar */}
          <div className="space-y-4">
            {selectedInterview ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Interview Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Resource Info */}
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Resource</p>
                      <button
                        onClick={() => navigate(`/resources/${selectedInterview.resource_id}`)}
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <User className="w-4 h-4" />
                        {selectedInterview.resource?.first_name} {selectedInterview.resource?.last_name}
                      </button>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedInterview.resource?.role} • {selectedInterview.resource?.seniority}
                      </p>
                    </div>

                    {/* Status */}
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Status</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm ${getStatusColor(selectedInterview.status)}`}>
                        {getStatusLabel(selectedInterview.status)}
                      </span>
                    </div>

                    {/* Confirmed Details */}
                    {selectedInterview.confirmed_slot && (
                      <>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Date & Time</p>
                          <p className="font-medium">
                            {new Date(selectedInterview.confirmed_slot.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                          <p className="text-gray-600">
                            {selectedInterview.confirmed_slot.time} ({selectedInterview.confirmed_slot.timezone})
                          </p>
                        </div>
                        {selectedInterview.confirmed_platform && (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Platform</p>
                            <div className="flex items-center gap-2">
                              {getPlatformIcon(selectedInterview.confirmed_platform)}
                              <span className="font-medium">{selectedInterview.confirmed_platform}</span>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Proposed Slots (if pending) */}
                    {['pending', 'pending_pm'].includes(selectedInterview.status) && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Proposed Time Slots</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {selectedInterview.proposed_slots?.slice(0, 8).map((slot, idx) => (
                            <div key={idx} className="text-sm bg-gray-50 px-2 py-1 rounded">
                              {new Date(slot.date).toLocaleDateString()} at {slot.time}
                            </div>
                          ))}
                          {selectedInterview.proposed_slots?.length > 8 && (
                            <p className="text-xs text-gray-500">
                              +{selectedInterview.proposed_slots.length - 8} more slots
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Duration */}
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Duration</p>
                      <p className="font-medium">{selectedInterview.duration_minutes} minutes</p>
                    </div>

                    {/* Preferred Platforms */}
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Preferred Platforms</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedInterview.preferred_platforms?.map(platform => (
                          <span 
                            key={platform}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs"
                          >
                            {getPlatformIcon(platform)}
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Messages */}
                    {(selectedInterview.pm_message || selectedInterview.rm_message) && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Messages</p>
                        {selectedInterview.pm_message && (
                          <div className="bg-blue-50 p-2 rounded text-sm mb-2">
                            <p className="text-xs text-blue-600 mb-1">From PM:</p>
                            {selectedInterview.pm_message}
                          </div>
                        )}
                        {selectedInterview.rm_message && (
                          <div className="bg-green-50 p-2 rounded text-sm">
                            <p className="text-xs text-green-600 mb-1">From RM:</p>
                            {selectedInterview.rm_message}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Actions Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {/* Send Calendar Invite - for confirmed interviews */}
                    {selectedInterview.status === 'confirmed' && !selectedInterview.invite_sent_at && (
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          const token = localStorage.getItem('token');
                          try {
                            const response = await axios.post(`${API}/interviews/${selectedInterview.id}/send-invite`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Calendar invite sent to all participants!');
                            if (response.data.meet_link) {
                              toast.info(`Google Meet link: ${response.data.meet_link}`);
                            }
                            fetchInterviews();
                          } catch (error) {
                            if (error.response?.data?.detail?.includes('not connected')) {
                              toast.error('Please connect your Google Calendar first in Profile settings');
                            } else {
                              toast.error(error.response?.data?.detail || 'Failed to send invite');
                            }
                          }
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Send Calendar Invite
                      </Button>
                    )}
                    
                    {/* Show Meet link if already sent */}
                    {selectedInterview.meet_link && (
                      <a
                        href={selectedInterview.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200 hover:bg-green-100"
                      >
                        <Video className="w-4 h-4" />
                        Join Google Meet
                      </a>
                    )}
                    
                    {selectedInterview.calendar_link && (
                      <a
                        href={selectedInterview.calendar_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-blue-600 hover:underline text-sm"
                      >
                        View in Google Calendar
                      </a>
                    )}
                    
                    {/* Microsoft Teams link */}
                    {selectedInterview.teams_link && (
                      <a
                        href={selectedInterview.teams_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100"
                      >
                        <Video className="w-4 h-4" />
                        Join Microsoft Teams
                      </a>
                    )}
                    
                    {/* Email Reminders Section */}
                    {selectedInterview.status === 'confirmed' && selectedInterview.confirmed_slot && (
                      <div className="border-t pt-3 mt-3">
                        <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Email Reminders
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">24h reminder</span>
                            {selectedInterview.reminder_24h_sent ? (
                              <span className="text-green-600 flex items-center gap-1 text-xs">
                                <Check className="w-3 h-3" /> Sent
                              </span>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs h-7"
                                onClick={async () => {
                                  const token = localStorage.getItem('token');
                                  try {
                                    await axios.post(`${API}/interviews/${selectedInterview.id}/send-reminder?reminder_type=24h`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success('24h reminder sent!');
                                    fetchInterviews();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to send reminder');
                                  }
                                }}
                              >
                                Send now
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">1h reminder</span>
                            {selectedInterview.reminder_1h_sent ? (
                              <span className="text-green-600 flex items-center gap-1 text-xs">
                                <Check className="w-3 h-3" /> Sent
                              </span>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs h-7"
                                onClick={async () => {
                                  const token = localStorage.getItem('token');
                                  try {
                                    await axios.post(`${API}/interviews/${selectedInterview.id}/send-reminder?reminder_type=1h`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success('1h reminder sent!');
                                    fetchInterviews();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to send reminder');
                                  }
                                }}
                              >
                                Send now
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Reminders include meeting link and .ics file attachment
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* RM Actions for pending interviews */}
                    {isRM && selectedInterview.status === 'pending' && !selectedInterview.is_requester && (
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => setShowResponseModal(true)}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Respond to Request
                      </Button>
                    )}

                    {/* Notes */}
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setShowNotesModal(true)}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {selectedInterview.notes ? 'View/Edit Notes' : 'Add Notes'}
                    </Button>

                    {/* PM Evaluation for completed interviews */}
                    {isPM && selectedInterview.status === 'confirmed' && selectedInterview.is_requester && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => setShowEvaluationModal(true)}
                      >
                        <Star className="w-4 h-4 mr-2" />
                        {selectedInterview.evaluation ? 'View Evaluation' : 'Complete & Evaluate'}
                      </Button>
                    )}

                    {/* Message */}
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/messages', {
                        state: {
                          targetCompanyId: selectedInterview.is_requester 
                            ? selectedInterview.resource_company_id
                            : selectedInterview.requester_company_id
                        }
                      })}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                    
                    {/* Reschedule Button - for confirmed interviews */}
                    {selectedInterview.status === 'confirmed' && (
                      <Button 
                        variant="outline" 
                        className="w-full text-orange-600 border-orange-200 hover:bg-orange-50"
                        onClick={() => setShowRescheduleModal(true)}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reschedule Interview
                      </Button>
                    )}
                    
                    {/* Cancel Button - for pending or confirmed interviews */}
                    {['pending', 'confirmed', 'pending_pm'].includes(selectedInterview.status) && (
                      <Button 
                        variant="outline" 
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                        onClick={async () => {
                          if (!confirm('Are you sure you want to cancel this interview?')) return;
                          const token = localStorage.getItem('token');
                          try {
                            await axios.put(`${API}/interviews/${selectedInterview.id}/cancel`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Interview cancelled');
                            fetchInterviews();
                            setSelectedInterview(null);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to cancel interview');
                          }
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel Interview
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-2 border-dashed">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    Select an interview to view details
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Response Modal for RM */}
      <ResponseModal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        interview={selectedInterview}
        onSuccess={() => {
          fetchInterviews();
          setShowResponseModal(false);
        }}
      />

      {/* Notes Modal */}
      <NotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        interview={selectedInterview}
        onSuccess={(updatedNotes) => {
          setSelectedInterview(prev => ({ ...prev, notes: updatedNotes }));
          fetchInterviews();
        }}
      />

      {/* Evaluation Modal */}
      <EvaluationModal
        isOpen={showEvaluationModal}
        onClose={() => setShowEvaluationModal(false)}
        interview={selectedInterview}
        onSuccess={() => {
          fetchInterviews();
          setShowEvaluationModal(false);
        }}
      />
      
      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        interview={selectedInterview}
        onSuccess={() => {
          fetchInterviews();
          setShowRescheduleModal(false);
          setSelectedInterview(null);
        }}
      />
    </div>
  );
};

// Response Modal Component
const ResponseModal = ({ isOpen, onClose, interview, onSuccess }) => {
  const [action, setAction] = useState('confirm');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (interview?.preferred_platforms?.length > 0) {
      setSelectedPlatform(interview.preferred_platforms[0]);
    }
  }, [interview]);

  const handleSubmit = async () => {
    if (action === 'confirm' && (!selectedSlot || !selectedPlatform)) {
      toast.error('Please select a time slot and platform');
      return;
    }

    setSending(true);
    const token = localStorage.getItem('token');

    try {
      const emailList = additionalEmails
        .split(/[,;\s]+/)
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));

      await axios.post(`${API}/interviews/${interview.id}/respond`, {
        interview_id: interview.id,
        action,
        confirmed_slot: selectedSlot ? {
          date: selectedSlot.date,
          time: selectedSlot.time,
          timezone: selectedSlot.timezone
        } : null,
        confirmed_platform: selectedPlatform,
        additional_invitees: emailList,
        message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Interview ${action}ed successfully!`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    } finally {
      setSending(false);
    }
  };

  if (!interview) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Respond to Interview Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Action Selection */}
          <div>
            <Label>Action</Label>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setAction('confirm')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                  action === 'confirm'
                    ? 'bg-green-50 border-green-400 text-green-700'
                    : 'bg-white border-gray-300'
                }`}
              >
                <Check className="w-4 h-4 inline mr-1" />
                Confirm
              </button>
              <button
                onClick={() => setAction('cancel')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                  action === 'cancel'
                    ? 'bg-red-50 border-red-400 text-red-700'
                    : 'bg-white border-gray-300'
                }`}
              >
                <X className="w-4 h-4 inline mr-1" />
                Decline
              </button>
            </div>
          </div>

          {action === 'confirm' && (
            <>
              {/* Select Time Slot */}
              <div>
                <Label>Select a Time Slot</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {interview.proposed_slots?.map((slot, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-2 text-sm rounded-lg border transition-all text-left ${
                        selectedSlot === slot
                          ? 'bg-green-50 border-green-400'
                          : 'bg-white border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <p className="font-medium">{new Date(slot.date).toLocaleDateString()}</p>
                      <p className="text-gray-500">{slot.time} ({slot.timezone})</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Select Platform */}
              <div>
                <Label>Select Platform</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {interview.preferred_platforms?.map(platform => (
                    <button
                      key={platform}
                      onClick={() => setSelectedPlatform(platform)}
                      className={`px-3 py-2 rounded-lg border transition-all ${
                        selectedPlatform === platform
                          ? 'bg-blue-50 border-blue-400 text-blue-700'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Invitees */}
              <div>
                <Label>Additional Invitees (optional)</Label>
                <Input
                  placeholder="resource@company.com, colleague@company.com"
                  value={additionalEmails}
                  onChange={(e) => setAdditionalEmails(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add the resource's email and any other team members
                </p>
              </div>
            </>
          )}

          {/* Message */}
          <div>
            <Label>Message (optional)</Label>
            <Textarea
              placeholder="Any additional information..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={sending}
            className={action === 'confirm' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {sending ? 'Sending...' : action === 'confirm' ? 'Confirm Interview' : 'Decline Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Notes Modal Component
const NotesModal = ({ isOpen, onClose, interview, onSuccess }) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (interview) {
      setNotes(interview.notes || '');
    }
  }, [interview]);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(`${API}/interviews/${interview.id}/notes`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { notes }
      });
      toast.success('Notes saved');
      onSuccess(notes);
      onClose();
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  if (!interview) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Interview Notes</DialogTitle>
        </DialogHeader>
        <div>
          <Textarea
            placeholder="Add your notes about this interview..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={8}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Notes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Evaluation Modal Component
const EvaluationModal = ({ isOpen, onClose, interview, onSuccess }) => {
  const [evaluation, setEvaluation] = useState({
    rating: 0,
    technical_fit: '',
    cultural_fit: '',
    communication: '',
    next_steps: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (interview?.evaluation) {
      setEvaluation(interview.evaluation);
    }
  }, [interview]);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(`${API}/interviews/${interview.id}/evaluate`, evaluation, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evaluation saved');
      onSuccess();
    } catch (error) {
      toast.error('Failed to save evaluation');
    } finally {
      setSaving(false);
    }
  };

  if (!interview) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Interview Evaluation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Rating */}
          <div>
            <Label>Overall Rating</Label>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setEvaluation(prev => ({ ...prev, rating: star }))}
                  className="p-1"
                >
                  <Star 
                    className={`w-6 h-6 ${
                      star <= evaluation.rating 
                        ? 'fill-yellow-400 text-yellow-400' 
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Technical Fit */}
          <div>
            <Label>Technical Fit</Label>
            <select
              value={evaluation.technical_fit}
              onChange={(e) => setEvaluation(prev => ({ ...prev, technical_fit: e.target.value }))}
              className="w-full mt-1 border rounded-md px-3 py-2"
            >
              <option value="">Select...</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </select>
          </div>

          {/* Next Steps */}
          <div>
            <Label>Next Steps</Label>
            <select
              value={evaluation.next_steps}
              onChange={(e) => setEvaluation(prev => ({ ...prev, next_steps: e.target.value }))}
              className="w-full mt-1 border rounded-md px-3 py-2"
            >
              <option value="">Select...</option>
              <option value="hire">Proceed to Pre-Reservation</option>
              <option value="shortlist">Add to Shortlist</option>
              <option value="maybe">Maybe - Need more info</option>
              <option value="pass">Pass - Not a fit</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <Label>Additional Notes</Label>
            <Textarea
              placeholder="Any other observations..."
              value={evaluation.notes}
              onChange={(e) => setEvaluation(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Evaluation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Reschedule Modal Component
const RescheduleModal = ({ isOpen, onClose, interview, onSuccess }) => {
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Generate next 10 business days
  const getBusinessDays = () => {
    const days = [];
    let date = new Date();
    while (days.length < 10) {
      date.setDate(date.getDate() + 1);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(new Date(date));
      }
    }
    return days;
  };

  const businessDays = getBusinessDays();
  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00'
  ];

  const addSlot = () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select both date and time');
      return;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const newSlot = { date: selectedDate, time: selectedTime, timezone };
    
    // Check for duplicates
    const exists = selectedSlots.some(s => s.date === newSlot.date && s.time === newSlot.time);
    if (exists) {
      toast.error('This slot is already added');
      return;
    }
    
    setSelectedSlots([...selectedSlots, newSlot]);
    setSelectedTime('');
  };

  const removeSlot = (index) => {
    setSelectedSlots(selectedSlots.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Please add at least one time slot');
      return;
    }

    setSending(true);
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/interviews/${interview.id}/reschedule`, {
        new_slots: selectedSlots,
        message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Reschedule request sent successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setSending(false);
    }
  };

  if (!interview) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-orange-500" />
            Reschedule Interview
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Current slot info */}
          {interview.confirmed_slot && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500 mb-1">Current scheduled time:</p>
              <p className="font-medium text-red-600 line-through">
                {new Date(interview.confirmed_slot.date).toLocaleDateString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric'
                })} at {interview.confirmed_slot.time} ({interview.confirmed_slot.timezone})
              </p>
            </div>
          )}
          
          {/* New slots selection */}
          <div>
            <Label>Propose new time slots</Label>
            <div className="flex gap-2 mt-2">
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="flex-1 border rounded-md px-3 py-2"
              >
                <option value="">Select date...</option>
                {businessDays.map((day) => (
                  <option key={day.toISOString()} value={day.toISOString().split('T')[0]}>
                    {day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </option>
                ))}
              </select>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-28 border rounded-md px-3 py-2"
              >
                <option value="">Time...</option>
                {timeSlots.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              <Button type="button" onClick={addSlot} size="sm">
                Add
              </Button>
            </div>
          </div>
          
          {/* Selected slots */}
          {selectedSlots.length > 0 && (
            <div className="space-y-2">
              <Label>Proposed slots ({selectedSlots.length})</Label>
              {selectedSlots.map((slot, index) => (
                <div key={index} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md">
                  <span className="text-sm">
                    {new Date(slot.date).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric'
                    })} at {slot.time}
                  </span>
                  <button
                    onClick={() => removeSlot(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Message */}
          <div>
            <Label>Message (optional)</Label>
            <Textarea
              placeholder="Explain why you need to reschedule..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
          
          <div className="bg-orange-50 rounded-lg p-3 text-sm text-orange-700">
            <strong>Note:</strong> The other party will be notified and asked to confirm one of your proposed slots.
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={sending || selectedSlots.length === 0}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {sending ? 'Sending...' : 'Request Reschedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewsPage;
